import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import Icon from '@/components/Icon';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/session';
import {
  computeProductStatus,
  isTrackingMode,
  type ProductRow,
  type TrackingMode,
} from '@/lib/inventory';
import {
  assignBarcodeToProduct,
  recordScanDecrement,
  recordScanUndo,
} from '@/lib/scan';

// ============================================================
// ScanConfirmPage — Phase 2, Epic 2B.2 + 2B.3
// ============================================================
// One route, three modes:
//
//   /scan/confirm/match?productId=...&barcode=...
//     The 2B.2 happy path. Writes the scan_decrement event on
//     mount, shows a confirmation card with the product, the
//     post-decrement count, and an undo button. Undo writes a
//     compensating manual_adjustment with notes='scan-undo'
//     (audit trail stays honest about what happened).
//
//   /scan/confirm/unknown?barcode=...
//     The 2B.3 flow. Renders a searchable, categorized list
//     of products. Tapping a product calls set_product_barcode
//     RPC to assign the scanned barcode to that product, then
//     navigates back to /scan. (The new product's barcode
//     will then resolve via 2B.2 on subsequent scans.)
//
//   /scan/confirm/multi?barcode=...
//     Rare. Multiple products share the scanned barcode (no
//     uniqueness constraint). Shows just the matching products
//     and asks the user to pick one. After pick: same as the
//     'match' mode — write the event for the chosen product.
//
// Camera lifecycle: the ScanPage stops the camera before
// navigating here, so this page never has a live camera.
// "Scan another" is an explicit action that takes the user
// back to /scan, which re-prompts (or auto-starts if already
// granted).
//
// Notably NOT here:
//   - The match-mode write happens client-side via the same
//     supabase.from('inventory_events').insert pattern as the
//     2A.3 stepper. Same trigger, same atomicity guarantees.
//   - We don't pre-fetch the product on the way in — we already
//     have the productId from the URL, but we still need the
//     product row to render name / count / category. So the
//     match mode does a single SELECT on mount.
// ============================================================

type Mode = 'match' | 'unknown' | 'multi';

function isMode(value: string | undefined): value is Mode {
  return value === 'match' || value === 'unknown' || value === 'multi';
}

export default function ScanConfirmPage() {
  const params = useParams<{ mode: string }>();
  const [searchParams] = useSearchParams();

  const mode = params.mode;
  const barcode = searchParams.get('barcode') ?? '';
  const productId = searchParams.get('productId') ?? '';

  if (!isMode(mode)) {
    // Unrecognized mode — bounce to /scan rather than render
    // an error. Hitting this means a stale link or hand-typed
    // URL; the camera is the right destination.
    return <Navigate to="/scan" replace />;
  }

  if (mode === 'match') {
    if (productId === '' || barcode === '') {
      return <Navigate to="/scan" replace />;
    }
    return <MatchMode productId={productId} barcode={barcode} />;
  }

  if (barcode === '') {
    return <Navigate to="/scan" replace />;
  }

  return mode === 'unknown' ? (
    <UnknownMode barcode={barcode} />
  ) : (
    <MultiMode barcode={barcode} />
  );
}

// ============================================================
// MATCH mode — write the event, show confirmation, allow undo
// ============================================================

type MatchState =
  | { kind: 'writing' }
  | { kind: 'product_failed'; message: string }
  | { kind: 'event_failed'; product: ProductRow; message: string }
  | { kind: 'negative_count'; product: ProductRow }
  | { kind: 'logged'; product: ProductRow }
  | { kind: 'undoing'; product: ProductRow }
  | { kind: 'undone'; product: ProductRow }
  | { kind: 'undo_failed'; product: ProductRow; message: string };

interface MatchModeProps {
  productId: string;
  barcode: string;
}

function MatchMode({ productId, barcode }: MatchModeProps) {
  const navigate = useNavigate();
  const { activeMembership, userId } = useSession();
  const clinicId = activeMembership.clinic_id;
  const [state, setState] = useState<MatchState>({ kind: 'writing' });

  // Single-shot: fetch the product, then write the event. We
  // don't refetch the product after the event because the
  // trigger updates products.count atomically and we already
  // know the post-event count locally (current count - 1).
  // The cancellation flag covers the React StrictMode
  // double-invoke without doing the write twice.
  useEffect(() => {
    let cancelled = false;
    let didWrite = false;

    async function run(): Promise<void> {
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('id', productId)
        .maybeSingle();

      if (cancelled) return;

      if (productError) {
        console.error('[viashift] product fetch failed:', productError);
        setState({ kind: 'product_failed', message: productError.message });
        return;
      }
      if (!product) {
        setState({
          kind: 'product_failed',
          message: 'Product not found in this clinic.',
        });
        return;
      }

      // Guard against StrictMode double-invoke: the write
      // happens at most once per mount cycle. The flag is in
      // the closure, separate from React state, so it survives
      // even if the effect runs again before state catches up.
      if (didWrite) return;
      didWrite = true;

      const result = await recordScanDecrement(clinicId, productId, userId);

      if (cancelled) return;

      if (result.kind === 'ok') {
        // Decrement happened. Render the post-decrement count
        // by subtracting 1 from what we just fetched.
        setState({
          kind: 'logged',
          product: { ...product, count: product.count - 1 },
        });
      } else if (result.kind === 'negative_count') {
        setState({ kind: 'negative_count', product });
      } else {
        setState({
          kind: 'event_failed',
          product,
          message: result.message,
        });
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [clinicId, productId, userId]);

  const onUndo = useCallback(async (): Promise<void> => {
    if (state.kind !== 'logged') return;
    setState({ kind: 'undoing', product: state.product });
    const result = await recordScanUndo(clinicId, productId, userId);
    if (result.ok) {
      setState({
        kind: 'undone',
        // Reverse the local decrement so the displayed count
        // matches reality.
        product: { ...state.product, count: state.product.count + 1 },
      });
    } else {
      setState({
        kind: 'undo_failed',
        product: state.product,
        message: result.message,
      });
    }
  }, [clinicId, productId, state, userId]);

  const onScanAnother = useCallback(() => navigate('/scan'), [navigate]);
  const onGoToInventory = useCallback(
    () => navigate('/inventory'),
    [navigate],
  );

  if (state.kind === 'writing') {
    return (
      <ConfirmShell title="Logging scan…">
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </ConfirmShell>
    );
  }

  if (state.kind === 'product_failed') {
    return (
      <ConfirmShell title="Couldn't load product">
        <ErrorBlock message={state.message} />
        <PrimaryActions
          onScanAnother={onScanAnother}
          onGoToInventory={onGoToInventory}
        />
      </ConfirmShell>
    );
  }

  if (state.kind === 'event_failed') {
    return (
      <ConfirmShell title="Couldn't log scan">
        <ProductCard
          product={state.product}
          barcode={barcode}
          status="error"
        />
        <ErrorBlock message={state.message} />
        <PrimaryActions
          onScanAnother={onScanAnother}
          onGoToInventory={onGoToInventory}
        />
      </ConfirmShell>
    );
  }

  if (state.kind === 'negative_count') {
    return (
      <ConfirmShell title="Bin already empty">
        <ProductCard
          product={state.product}
          barcode={barcode}
          status="warn"
        />
        <div className="mt-3 text-[12px] text-slate-600 px-1 text-center leading-relaxed">
          The count for this product is already 0. Nothing was changed. Use
          the inventory page to log a restock when the bin is refilled.
        </div>
        <PrimaryActions
          onScanAnother={onScanAnother}
          onGoToInventory={onGoToInventory}
        />
      </ConfirmShell>
    );
  }

  if (state.kind === 'undoing') {
    return (
      <ConfirmShell title="Undoing…">
        <ProductCard
          product={state.product}
          barcode={barcode}
          status="ok"
        />
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </ConfirmShell>
    );
  }

  if (state.kind === 'undone') {
    return (
      <ConfirmShell title="Scan undone">
        <ProductCard
          product={state.product}
          barcode={barcode}
          status="warn"
        />
        <div className="mt-3 text-[12px] text-slate-600 px-1 text-center">
          Count restored to {state.product.count}.
        </div>
        <PrimaryActions
          onScanAnother={onScanAnother}
          onGoToInventory={onGoToInventory}
        />
      </ConfirmShell>
    );
  }

  if (state.kind === 'undo_failed') {
    return (
      <ConfirmShell title="Couldn't undo">
        <ProductCard
          product={state.product}
          barcode={barcode}
          status="error"
        />
        <ErrorBlock message={state.message} />
        <PrimaryActions
          onScanAnother={onScanAnother}
          onGoToInventory={onGoToInventory}
        />
      </ConfirmShell>
    );
  }

  // state.kind === 'logged'
  return (
    <ConfirmShell title="Logged">
      <ProductCard product={state.product} barcode={barcode} status="ok" />
      <button
        type="button"
        onClick={() => {
          void onUndo();
        }}
        className="mt-3 w-full h-11 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 active:bg-slate-100 transition text-sm font-medium text-slate-700 flex items-center justify-center gap-2"
      >
        <Icon name="x" className="w-4 h-4" strokeWidth={2} />
        Undo
      </button>
      <PrimaryActions
        onScanAnother={onScanAnother}
        onGoToInventory={onGoToInventory}
      />
    </ConfirmShell>
  );
}

// ============================================================
// UNKNOWN mode — search & assign barcode (2B.3)
// ============================================================

type UnknownState =
  | { kind: 'loading' }
  | { kind: 'load_failed'; message: string }
  | {
      kind: 'ready';
      categories: CategoryWithProducts[];
      filter: string;
    }
  | { kind: 'assigning'; productId: string }
  | { kind: 'assign_conflict'; productId: string }
  | { kind: 'assign_failed'; productId: string; message: string };

interface CategoryWithProducts {
  id: string;
  code: string;
  name: string;
  tracking_mode: TrackingMode;
  products: ProductRow[];
}

function UnknownMode({ barcode }: { barcode: string }) {
  const navigate = useNavigate();
  const { activeMembership } = useSession();
  const clinicId = activeMembership.clinic_id;
  const [state, setState] = useState<UnknownState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    async function run(): Promise<void> {
      const [catsResult, prodsResult] = await Promise.all([
        supabase
          .from('categories')
          .select('*')
          .eq('clinic_id', clinicId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('products')
          .select('*')
          .eq('clinic_id', clinicId)
          .order('name', { ascending: true }),
      ]);
      if (cancelled) return;
      if (catsResult.error) {
        setState({ kind: 'load_failed', message: catsResult.error.message });
        return;
      }
      if (prodsResult.error) {
        setState({ kind: 'load_failed', message: prodsResult.error.message });
        return;
      }
      const categories: CategoryWithProducts[] = [];
      for (const cat of catsResult.data ?? []) {
        if (!isTrackingMode(cat.tracking_mode)) continue;
        const products = (prodsResult.data ?? []).filter(
          (p) => p.category_id === cat.id,
        );
        categories.push({
          id: cat.id,
          code: cat.code,
          name: cat.name,
          tracking_mode: cat.tracking_mode,
          products,
        });
      }
      setState({ kind: 'ready', categories, filter: '' });
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [clinicId]);

  const onAssign = useCallback(
    async (productId: string): Promise<void> => {
      setState({ kind: 'assigning', productId });
      const result = await assignBarcodeToProduct(
        clinicId,
        productId,
        barcode,
      );
      if (result.kind === 'ok') {
        // Done — back to scan. Subsequent scans of this barcode
        // will resolve via the match path.
        navigate('/scan');
      } else if (result.kind === 'conflict') {
        setState({ kind: 'assign_conflict', productId });
      } else {
        setState({
          kind: 'assign_failed',
          productId,
          message: result.message,
        });
      }
    },
    [barcode, clinicId, navigate],
  );

  const onScanAnother = useCallback(() => navigate('/scan'), [navigate]);

  if (state.kind === 'loading') {
    return (
      <ConfirmShell title="Unknown barcode">
        <BarcodeStrip barcode={barcode} />
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      </ConfirmShell>
    );
  }

  if (state.kind === 'load_failed') {
    return (
      <ConfirmShell title="Unknown barcode">
        <BarcodeStrip barcode={barcode} />
        <ErrorBlock message={state.message} />
        <PrimaryActions onScanAnother={onScanAnother} />
      </ConfirmShell>
    );
  }

  if (
    state.kind === 'assigning' ||
    state.kind === 'assign_conflict' ||
    state.kind === 'assign_failed'
  ) {
    return (
      <ConfirmShell title="Unknown barcode">
        <BarcodeStrip barcode={barcode} />
        {state.kind === 'assigning' && (
          <div className="flex flex-col items-center gap-2 py-6">
            <Spinner />
            <div className="text-[11px] text-slate-500">Linking…</div>
          </div>
        )}
        {state.kind === 'assign_conflict' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[12px] text-amber-800 leading-relaxed">
            This barcode is already assigned to another product in your
            clinic. Pick a different product or check the inventory page to
            see the existing assignment.
          </div>
        )}
        {state.kind === 'assign_failed' && (
          <ErrorBlock message={state.message} />
        )}
        <button
          type="button"
          onClick={() =>
            setState((s) =>
              s.kind === 'assigning' ||
              s.kind === 'assign_conflict' ||
              s.kind === 'assign_failed'
                ? // Reset back to ready so the user can pick again.
                  // We need to refetch categories — easiest is to
                  // re-trigger the loading state.
                  { kind: 'loading' }
                : s,
            )
          }
          className="mt-3 w-full h-11 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition text-sm font-medium text-slate-700"
        >
          Pick a different product
        </button>
        <PrimaryActions onScanAnother={onScanAnother} />
      </ConfirmShell>
    );
  }

  // state.kind === 'ready'
  return (
    <ConfirmShell title="Match this barcode">
      <BarcodeStrip barcode={barcode} />
      <div className="text-[12px] text-slate-600 leading-relaxed mt-2 mb-3">
        We don't recognize this barcode yet. Tap a product below to link it
        — next time, it'll resolve automatically.
      </div>
      <FilterableProductList
        categories={state.categories}
        filter={state.filter}
        onFilterChange={(f) =>
          setState((s) => (s.kind === 'ready' ? { ...s, filter: f } : s))
        }
        onPick={(id) => {
          void onAssign(id);
        }}
      />
      <PrimaryActions onScanAnother={onScanAnother} />
    </ConfirmShell>
  );
}

// ============================================================
// MULTI mode — pick which product the barcode belongs to
// ============================================================

type MultiState =
  | { kind: 'loading' }
  | { kind: 'load_failed'; message: string }
  | { kind: 'ready'; products: ProductRow[] };

function MultiMode({ barcode }: { barcode: string }) {
  const navigate = useNavigate();
  const { activeMembership } = useSession();
  const clinicId = activeMembership.clinic_id;
  const [state, setState] = useState<MultiState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    async function run(): Promise<void> {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('barcode', barcode)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        setState({ kind: 'load_failed', message: error.message });
        return;
      }
      setState({ kind: 'ready', products: data ?? [] });
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [clinicId, barcode]);

  const onPick = useCallback(
    (productId: string) => {
      navigate(
        `/scan/confirm/match?productId=${encodeURIComponent(
          productId,
        )}&barcode=${encodeURIComponent(barcode)}`,
        { replace: true },
      );
    },
    [barcode, navigate],
  );

  const onScanAnother = useCallback(() => navigate('/scan'), [navigate]);

  if (state.kind === 'loading') {
    return (
      <ConfirmShell title="Multiple matches">
        <BarcodeStrip barcode={barcode} />
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      </ConfirmShell>
    );
  }

  if (state.kind === 'load_failed') {
    return (
      <ConfirmShell title="Multiple matches">
        <BarcodeStrip barcode={barcode} />
        <ErrorBlock message={state.message} />
        <PrimaryActions onScanAnother={onScanAnother} />
      </ConfirmShell>
    );
  }

  return (
    <ConfirmShell title="Multiple matches">
      <BarcodeStrip barcode={barcode} />
      <div className="text-[12px] text-slate-600 leading-relaxed mt-2 mb-3">
        This barcode is on more than one product. Pick the one you scanned.
      </div>
      <div className="space-y-1.5">
        {state.products.map((p) => (
          <ProductButton key={p.id} product={p} onPick={() => onPick(p.id)} />
        ))}
      </div>
      <PrimaryActions onScanAnother={onScanAnother} />
    </ConfirmShell>
  );
}

// ============================================================
// Shared building blocks
// ============================================================

function ConfirmShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="p-4 pb-6">
      <h1 className="text-base font-semibold text-slate-900 mb-3">{title}</h1>
      {children}
    </div>
  );
}

const STATUS_META: Record<
  'ok' | 'warn' | 'error',
  { ring: string; bg: string; icon: 'check' | 'shield' | 'x'; text: string }
> = {
  ok: {
    ring: 'border-emerald-200',
    bg: 'bg-emerald-50',
    icon: 'check',
    text: 'text-emerald-700',
  },
  warn: {
    ring: 'border-amber-200',
    bg: 'bg-amber-50',
    icon: 'shield',
    text: 'text-amber-700',
  },
  error: {
    ring: 'border-red-200',
    bg: 'bg-red-50',
    icon: 'x',
    text: 'text-red-700',
  },
};

function ProductCard({
  product,
  barcode,
  status,
}: {
  product: ProductRow;
  barcode: string;
  status: 'ok' | 'warn' | 'error';
}) {
  const meta = STATUS_META[status];
  const computedStatus = computeProductStatus(product);
  return (
    <div
      className={`bg-white border ${meta.ring} rounded-2xl p-4 flex items-start gap-3`}
    >
      <div
        className={`w-9 h-9 shrink-0 rounded-xl ${meta.bg} flex items-center justify-center`}
      >
        <Icon
          name={meta.icon}
          className={`w-5 h-5 ${meta.text}`}
          strokeWidth={2}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-slate-900 leading-tight">
          {product.name}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
          {product.uom}
          {product.par !== null && <> · par {product.par}</>}
        </div>
        <div
          className={`text-[11px] text-slate-400 mt-1 truncate ${
            barcode === 'manual' ? 'italic' : 'font-mono'
          }`}
        >
          {barcode === 'manual' ? 'Manual log (tap-to-log)' : barcode}
        </div>
      </div>
      <div
        className={`shrink-0 text-right ${computedStatus === 'critical' ? 'text-red-600' : computedStatus === 'low' ? 'text-amber-600' : 'text-slate-900'}`}
      >
        <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">
          Count
        </div>
        <div className="text-2xl font-bold tabular-nums leading-none">
          {product.count}
        </div>
      </div>
    </div>
  );
}

function BarcodeStrip({ barcode }: { barcode: string }) {
  // Tap-to-log entries set barcode=manual so the audit context
  // is captured but the strip renders as a label rather than
  // a fake barcode value.
  const isManual = barcode === 'manual';
  return (
    <div className="bg-slate-100 rounded-xl px-3 py-2 flex items-center gap-2">
      <Icon
        name={isManual ? 'cart' : 'scan'}
        className="w-4 h-4 text-slate-500"
        strokeWidth={2}
      />
      <div
        className={`text-[12px] truncate ${isManual ? 'text-slate-700' : 'font-mono text-slate-700'}`}
      >
        {isManual ? 'Manual log (tap-to-log)' : barcode}
      </div>
    </div>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3">
      <div className="text-[12px] font-medium text-red-700">
        Something went wrong
      </div>
      <div className="text-[11px] text-red-600 mt-0.5 font-mono break-words">
        {message}
      </div>
    </div>
  );
}

function PrimaryActions({
  onScanAnother,
  onGoToInventory,
}: {
  onScanAnother: () => void;
  onGoToInventory?: () => void;
}) {
  return (
    <div className="mt-5 flex flex-col gap-2">
      <button
        type="button"
        onClick={onScanAnother}
        className="w-full h-11 rounded-xl bg-action hover:bg-action-hover active:bg-action-active transition text-sm font-semibold text-white flex items-center justify-center gap-2"
      >
        <Icon name="scan" className="w-4 h-4" strokeWidth={2.25} />
        Scan another
      </button>
      {onGoToInventory !== undefined && (
        <button
          type="button"
          onClick={onGoToInventory}
          className="w-full h-11 rounded-xl bg-slate-100 hover:bg-slate-200 transition text-sm font-medium text-slate-700"
        >
          Go to inventory
        </button>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="w-7 h-7 border-[3px] border-slate-200 border-t-action rounded-full animate-spin"
      role="status"
      aria-label="Loading"
    />
  );
}

interface FilterableProductListProps {
  categories: CategoryWithProducts[];
  filter: string;
  onFilterChange: (f: string) => void;
  onPick: (productId: string) => void;
}

function FilterableProductList({
  categories,
  filter,
  onFilterChange,
  onPick,
}: FilterableProductListProps) {
  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === '') return categories;
    return categories
      .map((cat) => ({
        ...cat,
        products: cat.products.filter((p) =>
          p.name.toLowerCase().includes(needle),
        ),
      }))
      .filter((cat) => cat.products.length > 0);
  }, [categories, filter]);

  return (
    <div>
      <div className="relative mb-3">
        <input
          type="text"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          placeholder="Search products…"
          className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm placeholder:text-slate-400 focus:outline-none focus:border-action focus:ring-2 focus:ring-action/20"
        />
        <Icon
          name="search"
          className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          strokeWidth={2}
        />
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-[12px] text-slate-400">
          No products match "{filter}".
        </div>
      ) : (
        filtered.map((cat) => (
          <div key={cat.id} className="mb-4 last:mb-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 px-1">
              {cat.code} · {cat.name}
            </div>
            <div className="space-y-1.5">
              {cat.products.map((p) => (
                <ProductButton
                  key={p.id}
                  product={p}
                  onPick={() => onPick(p.id)}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ProductButton({
  product,
  onPick,
}: {
  product: ProductRow;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 hover:border-action hover:bg-action-weak/40 active:scale-[0.99] transition flex items-center gap-3"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-900 truncate">
          {product.name}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {product.uom}
          {product.par !== null && <> · par {product.par}</>}
        </div>
      </div>
      <div className="text-[15px] font-semibold text-slate-700 tabular-nums shrink-0">
        {product.count}
      </div>
    </button>
  );
}
